// ✅ ALL normal imports FIRST
import React, { useEffect, lazy, Suspense } from "react";
import CookieConsentBanner from "./components/common/CookieConsentBanner";
import { BrowserRouter as Router, useLocation } from "react-router-dom";

import AuthProviderNew from "./components/auth/AuthProvider";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import { ThemeProvider } from "./context/ThemeContext";

import ScrollToTop from "./components/common/ScrollToTop";
import PageBackground from "./components/common/PageBackground";
import "./styles/Buttons.css";

import { analyticsService } from "./services/analyticsService";
import { initGA } from "./analytics";
import { shouldShowHeaderFooter, isStandaloneRoute } from "./utils/navigation";
import { initializeCreditMigration } from "./utils/creditMigration";
import Header from "./components/Header";
import Home from "./pages/Home";
import Footer from "./components/Footer";
import PageLoader from "./components/common/PageLoader";

// ✅ THEN lazy imports
const EmailVerificationHandler = lazy(() => import("./components/auth/EmailVerificationHandler"));
const DataPreloader = lazy(() => import("./components/common/DataPreloader"));
// AppRoutes pre-imported so Blog/BlogPost have no chunk delay
const appRoutesChunk = import("./routes/AppRoutes");
const AppRoutes = lazy(() => appRoutesChunk);

// ─── Safari-safe idle callback helper ────────────────────────────────────────
// requestIdleCallback is not supported in Safari — always use this wrapper
function onIdle(cb: () => void): void {
  if ("requestIdleCallback" in window) {
    (window as any).requestIdleCallback(cb);
  } else {
    setTimeout(cb, 200);
  }
}

// ─────────────────────────────────────────────────────────────────────────────

function AppContent(): JSX.Element {
  const location = useLocation();

  const showHeaderFooter = shouldShowHeaderFooter(location.pathname);
  const isStandalone = isStandaloneRoute(location.pathname);

  const [loadPreloader, setLoadPreloader] = React.useState(false);

  // Delay heavy preloading by 2s after mount
  useEffect(() => {
    const timer = setTimeout(() => setLoadPreloader(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  // Track page view — non-blocking, Safari-safe
  useEffect(() => {
    onIdle(() => {
      const pageName = location.pathname.replace("/", "") || "home";
      analyticsService.pageViewed(pageName);
    });
  }, [location.pathname]);

  // Run credit migration in idle time (anon account creation is now handled by Edge Functions)
  useEffect(() => {
    onIdle(() => {
      initializeCreditMigration();
    });
  }, []);

  return (
    <PageBackground>
      <ScrollToTop />

      {!isStandalone && loadPreloader && (
        <Suspense fallback={null}>
          <DataPreloader />
        </Suspense>
      )}

      {showHeaderFooter && <Header />}

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {location.pathname === "/" ? (
          <Home />
        ) : (
          <Suspense fallback={<PageLoader />}>
            <AppRoutes />
          </Suspense>
        )}
      </div>

      {showHeaderFooter && <Footer />}

      <Suspense fallback={null}>
        <EmailVerificationHandler />
      </Suspense>

      {/* ✅ Cookie Consent Banner — AdSense & GDPR compliance */}
      <CookieConsentBanner />
    </PageBackground>
  );
}

const AppContentMemo = React.memo(AppContent);

function App(): JSX.Element {

  // ✅ Defer GA until interaction — massive performance win
  useEffect(() => {
    if (typeof window === "undefined") return;

    let initialized = false;
    const initOnInteraction = () => {
      if (initialized) return;
      initialized = true;

      // Clean up listeners
      window.removeEventListener('scroll', initOnInteraction);
      window.removeEventListener('mousemove', initOnInteraction);
      window.removeEventListener('touchstart', initOnInteraction);
      window.removeEventListener('keydown', initOnInteraction);

      // Initialize GA in idle time
      onIdle(() => initGA());
    };

    window.addEventListener('scroll', initOnInteraction, { passive: true });
    window.addEventListener('mousemove', initOnInteraction, { passive: true });
    window.addEventListener('touchstart', initOnInteraction, { passive: true });
    window.addEventListener('keydown', initOnInteraction, { passive: true });

    return () => {
      window.removeEventListener('scroll', initOnInteraction);
      window.removeEventListener('mousemove', initOnInteraction);
      window.removeEventListener('touchstart', initOnInteraction);
      window.removeEventListener('keydown', initOnInteraction);
    };
  }, []);

  // ✅ Performance + error tracking
  useEffect(() => {
    const startTime = performance.now();

    onIdle(() => analyticsService.appLaunched());

    window.addEventListener("load", () => {
      const loadTime = performance.now() - startTime;
      onIdle(() => analyticsService.pageLoadTime("app", loadTime));
    }, { once: true });

    const handleError = (event: ErrorEvent) => {
      analyticsService.javascriptError(event.error?.message || event.message);
    };
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      analyticsService.networkError(
        event.reason?.message || "Unhandled promise rejection"
      );
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    };
  }, []);

  // ✅ Firebase — lazy loaded, no duplicate GA
  // NOTE: initGA() above already handles G-F81GEFE6V4 via gtag
  // Firebase here is only for Firestore/Auth/other services — NOT analytics
  // If you only use Firebase for analytics, remove this block entirely
  useEffect(() => {
    if (typeof window === "undefined") return;

    onIdle(() => {
      import("./config/firebaseConfig").then(({ initFirebase }) => {
        initFirebase();
      });
    });
  }, []);

  return (
    <ThemeProvider>
      <AuthProviderNew>
        <SubscriptionProvider>
          <Router>
            <AppContentMemo />
          </Router>
        </SubscriptionProvider>
      </AuthProviderNew>
    </ThemeProvider>
  );
}

export default App;